import React from 'react';
import clsx from 'clsx';
import ReactHtmlParser from '@orrisroot/react-html-parser';
import styles from './GreenCheckboxList.module.scss';
import checkTick from './CheckTick.svg';

export type Option = {
  id: string | number;
  label: string;
  description?: React.ReactNode;
  badge?: string;
  link?: { text: string; onClick: () => void };
  // Rendered last, below the link - the renewal add-ons put their price here.
  footer?: React.ReactNode;
};

/**
 * Generic in the option type so a caller keeps whatever it hands in - the renewal add-ons
 * carry their full quote alongside the display fields, and get it back on selection.
 * "setSelected" only ever receives an updater, so it is typed narrower than a useState
 * setter; a useState setter still satisfies it.
 */
interface IGreenCheckboxList<T extends Option> {
  options: T[];
  selected: T[];
  setSelected: (_updater: (_prev: T[]) => T[]) => void;
  singleSelect?: boolean;
}

export const GreenCheckboxList = <T extends Option>({
  options,
  selected,
  setSelected,
  singleSelect = false,
}: IGreenCheckboxList<T>) => {
  const handleCheckBoxChange = (item: T) => {
    setSelected((prev) => {
      if (singleSelect) {
        return prev.some((newSelected) => newSelected.id === item.id)
          ? prev
          : [item];
      }

      return prev.some((newSelected) => newSelected.id === item.id)
        ? prev.filter((newSelected) => newSelected.id !== item.id)
        : [...prev, item];
    });
  };

  return (
    <fieldset className={styles.selectGroup}>
      {options.map((option) => {
        const isSelected = selected.some((item) => item.id === option.id);

        return (
          <button
            key={option.id}
            className={clsx(styles.checkbox, isSelected && styles.selected)}
            onClick={() => handleCheckBoxChange(option)}
            type="button"
          >
            {option.badge && (
              <span className={styles.badge}>{option.badge}</span>
            )}
            <div className={styles.indicator}>
              {isSelected && <img src={checkTick} alt="Check tick" />}
            </div>

            <div>
              <div className={styles.label}>{option.label}</div>
              {option?.description && (
                <div className={styles.description}>
                  {typeof option.description === 'string'
                    ? ReactHtmlParser(option.description)
                    : option.description}
                </div>
              )}
              {option.link && (
                <span
                  role="link"
                  tabIndex={0}
                  className={styles.link}
                  onClick={(e) => { e.stopPropagation(); option.link?.onClick(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); option.link?.onClick(); } }}
                >
                  {option.link.text}
                </span>
              )}
              {option?.footer && (
                <div className={styles.footer}>
                  {typeof option.footer === 'string'
                    ? ReactHtmlParser(option.footer)
                    : option.footer}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </fieldset>
)
};

export default GreenCheckboxList;
